import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const effectiveSlug = slug || "pandabuy-spreadsheet-guide";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [effectiveSlug]);

  const article = articles.find((a) => a.slug === effectiveSlug);

  if (!article) {
    return (
      <>
        <Navbar />
        <section className="relative pt-[140px] pb-20 bg-background-50 min-h-[60vh] flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="font-heading text-[32px] md:text-[40px] font-bold text-foreground-950 mb-4">
              Article Not Found
            </h1>
            <p className="text-foreground-500 mb-8">
              The article you are looking for does not exist or has been moved.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-[14px] font-label font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              Back to Homepage
            </Link>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      {/* Article Hero */}
      <section className="relative pt-[120px] pb-16 md:pt-[140px] md:pb-20 bg-background-50">
        <div className="max-w-[800px] mx-auto px-4 md:px-8">
          {/* Breadcrumb & Back Link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] font-label font-semibold text-foreground-500 hover:text-primary-500 transition-colors duration-200 mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-sm"></i>
            Back to Pandabuy.fit Homepage
          </Link>

          {/* Category Badge */}
          <span className="inline-block text-[11px] font-label font-semibold text-accent-600 bg-accent-50 px-3 py-1.5 rounded-full uppercase tracking-wider mb-5">
            {article.category}
          </span>

          {/* Article Title */}
          <h1 className="font-heading text-[36px] md:text-[48px] lg:text-[56px] leading-[1.12] text-foreground-950 font-bold tracking-[-0.02em] text-balance">
            {article.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mt-6 text-[13px] text-foreground-400 font-body">
            <span>Updated June 2026</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>{article.readTime}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>By Pandabuy.fit Editorial Team</span>
          </div>

          {/* Featured Image */}
          <div className="mt-10 rounded-2xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-[300px] md:h-[420px] object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="relative py-10 md:py-16 bg-background-50">
        <div className="max-w-[800px] mx-auto px-4 md:px-8">
          <div className="prose-custom">
            {/* Table of Contents */}
            <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-12">
              <h2 className="text-[15px] font-label font-bold text-foreground-900 uppercase tracking-wider mb-4">
                Table of Contents
              </h2>
              <ul className="space-y-2">
                {article.sections.map((section, i) => (
                  <li key={i}>
                    <a
                      href={`#section-${i + 1}`}
                      className="text-[14px] text-foreground-600 hover:text-primary-500 transition-colors duration-200 font-body cursor-pointer"
                    >
                      {i + 1}. {section.heading}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Article Sections */}
            {article.sections.map((section, i) => (
              <section key={i} id={`section-${i + 1}`} className="mb-12">
                <h2 className="font-heading text-[28px] md:text-[32px] font-bold text-foreground-950 mb-5 leading-tight">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, j) => (
                  <p key={j} className="text-[15px] text-foreground-600 leading-relaxed mb-4">
                    {para}
                  </p>
                ))}
              </section>
            ))}
          </div>

          {/* CTA at Bottom */}
          <div className="mt-14 bg-primary-800 rounded-2xl p-8 md:p-10 text-center">
            <h3 className="font-heading text-[24px] md:text-[28px] font-bold text-background-50 mb-3">
              {article.ctaTitle}
            </h3>
            <p className="text-[14px] text-background-50/70 leading-relaxed mb-6 max-w-[500px] mx-auto">
              {article.ctaText}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-7 py-3 bg-background-50 text-foreground-950 text-[14px] font-label font-semibold rounded-full hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Pandabuy Sheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-7 py-3 border border-background-50/20 text-background-50 text-[14px] font-label font-semibold rounded-full hover:bg-background-50/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Back to Homepage
                <i className="ri-arrow-left-line text-sm"></i>
              </Link>
            </div>
          </div>

          {/* Internal Link to Homepage */}
          <div className="mt-10 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[14px] text-foreground-500 hover:text-primary-500 transition-colors duration-200 font-body cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              Return to Pandabuy.fit — Your Pandabuy Spreadsheet Hub
            </Link>
          </div>
        </div>
      </article>

      <Footer />

      {/* Schema.org Article + BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Home",
                    item: import.meta.env.VITE_SITE_URL,
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: article.title,
                  },
                ],
              },
              {
                "@type": "Article",
                headline: article.title,
                description: article.description,
                author: {
                  "@type": "Organization",
                  name: "Pandabuy.fit Editorial Team",
                },
                datePublished: "2026-06-15",
                dateModified: "2026-06-29",
                publisher: {
                  "@type": "Organization",
                  name: "Pandabuy.fit",
                  logo: {
                    "@type": "ImageObject",
                    url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa4b8148a.jpeg",
                  },
                },
                image: article.imageUrl,
                mainEntityOfPage: {
                  "@type": "WebPage",
                  "@id": `${import.meta.env.VITE_SITE_URL}/article/${article.slug}`,
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}